import React, { Fragment, useLayoutEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

const BODY_RIGHT_SLIDEOUT_OPEN_CLASS = "has-right-slideout-open";
const RIGHT_SLIDEOUT_WIDTH_CSS_VAR = "--right-slideout-width";
const openRightSlideoutWidths = new Map<symbol, number | undefined>();

const updateRightSlideoutBodyState = () => {
  if (typeof document === "undefined") {
    return;
  }

  if (openRightSlideoutWidths.size === 0) {
    document.body.classList.remove(BODY_RIGHT_SLIDEOUT_OPEN_CLASS);
    document.body.style.removeProperty(RIGHT_SLIDEOUT_WIDTH_CSS_VAR);
    return;
  }

  const measuredWidths = [...openRightSlideoutWidths.values()].filter(
    (width): width is number => width !== undefined,
  );

  document.body.classList.add(BODY_RIGHT_SLIDEOUT_OPEN_CLASS);

  if (measuredWidths.length > 0) {
    document.body.style.setProperty(
      RIGHT_SLIDEOUT_WIDTH_CSS_VAR,
      `${Math.max(...measuredWidths)}px`,
    );
  } else {
    document.body.style.removeProperty(RIGHT_SLIDEOUT_WIDTH_CSS_VAR);
  }
};

const useRightSlideoutBodyState = ({
  open,
  slideFrom,
  panelElement,
}: {
  open: boolean;
  slideFrom: "left" | "right";
  panelElement: HTMLDivElement | null;
}) => {
  const slideoutIdRef = useRef(Symbol("right-slideout"));

  useLayoutEffect(() => {
    if (!open || slideFrom !== "right" || !panelElement || typeof document === "undefined") {
      return;
    }

    const slideoutId = slideoutIdRef.current;
    const updateSlideoutWidth = () => {
      const panelWidth = panelElement.getBoundingClientRect().width;

      if (panelWidth) {
        openRightSlideoutWidths.set(slideoutId, Math.ceil(panelWidth));
        updateRightSlideoutBodyState();
      }
    };

    openRightSlideoutWidths.set(slideoutId, undefined);
    updateSlideoutWidth();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateSlideoutWidth) : null;

    resizeObserver?.observe(panelElement);
    window.addEventListener("resize", updateSlideoutWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateSlideoutWidth);
      openRightSlideoutWidths.delete(slideoutId);
      updateRightSlideoutBodyState();
    };
  }, [open, panelElement, slideFrom]);
};

interface SlideoutProps {
  children: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  slideFrom?: "left" | "right"; // Optional prop to determine slide direction
  maxWidth?: string;
}

const SlideOut = ({
  children,
  open,
  setOpen,
  slideFrom = "right",
  maxWidth = "max-w-md",
}: SlideoutProps) => {
  // Determine slide-in and slide-out starting points
  const slideInFrom = slideFrom === "left" ? "-translate-x-full" : "translate-x-full";
  const slideOutTo = slideFrom === "left" ? "-translate-x-full" : "translate-x-full";
  // Determine panel positioning and padding
  const positionClass = slideFrom === "left" ? "left-0" : "right-0";

  const emptyRef = useRef(null);
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);
  useRightSlideoutBodyState({ open, slideFrom, panelElement });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 tw-boundary"
        initialFocus={emptyRef}
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden z-10">
          <div className="absolute inset-0 overflow-hidden">
            {/* Apply dynamic positioning and padding based on slide direction */}
            <div className={`pointer-events-none fixed inset-y-0 ${positionClass} flex max-w-full`}>
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom={slideInFrom}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo={slideOutTo}
              >
                <Dialog.Panel
                  ref={setPanelElement}
                  className={`pointer-events-auto w-screen ${maxWidth}`}
                >
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Dummy element that can take initial focus, otherwise the Headless UI <Dialog> component will set autofocus to the first input in the slide-out, which we don't want */}
                    <div ref={emptyRef} className="hidden" />
                    <div className="h-full">{children}</div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SlideOut;
